// src/pages/Dashboard/sections/NodeManagement.jsx
import React, { useState, useEffect } from 'react';
import * as nodeManagementApi from '../../../services/nodeManagementApi';
import LoadingSpinner from '../../../components/NodeManagement/components/LoadingSpinner';
import ErrorMessage from '../../../components/NodeManagement/components/ErrorMessage';
import SuccessMessage from '../../../components/NodeManagement/components/SuccessMessage';
import '../styles/nodeManagement.css';

/**
 * Node Management Section
 * Handles superior node binding and displays node relationships
 * Updated to work with the network stability maintenance fee system
 */
const NodeManagement = ({ 
  wallet, 
  activatedNodes, 
  selectedNode, 
  onNodeChange, 
  formatWalletAddress, 
  formatDate,
  refreshNodes
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingStatus, setIsCheckingStatus] = useState(true);
  const [bindingInProgress, setBindingInProgress] = useState(false);
  const [superiorCode, setSuperiorCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [nodeRelationships, setNodeRelationships] = useState({});
  const [bindingResult, setBindingResult] = useState(null);
  
  // Check relationship status for each node when component mounts or when nodes change
  useEffect(() => {
    if (activatedNodes.length > 0) {
      checkAllNodesBindingStatus();
    } else {
      setIsCheckingStatus(false);
    }
  }, [activatedNodes, wallet.connected]);
  
  // Check binding status for all nodes
  const checkAllNodesBindingStatus = async () => {
    if (!wallet.connected || activatedNodes.length === 0) return;
    
    setIsCheckingStatus(true);
    const relationships = {};
    
    try {
      // Use the correct blockchain network ID as a number, not a string
      // Convert network name to network ID if needed
      let networkId = wallet.networkId || 1;
      if (typeof networkId === 'string') {
        if (networkId === 'ethereum') networkId = 1;
        else if (networkId === 'bsc') networkId = 2;
        else if (networkId === 'solana') networkId = 3;
        else if (!isNaN(parseInt(networkId))) networkId = parseInt(networkId);
      }
      
      console.log("Using network ID:", networkId);
      
      // Check each node's binding status
      for (const node of activatedNodes) {
        try {
          const response = await nodeManagementApi.getNodeRelationshipStatus(
            node.id,
            networkId
          );
          
          relationships[node.id] = {
            isBound: response.isBound,
            relationshipData: response.relationshipData
          };
        } catch (err) {
          console.error(`Error checking binding status for node ${node.reference_code}:`, err);
          relationships[node.id] = { isBound: false, relationshipData: null };
        }
      }
      
      setNodeRelationships(relationships);
    } catch (err) {
      console.error('Error in checkAllNodesBindingStatus:', err);
    } finally {
      setIsCheckingStatus(false);
    }
  };
  
  // Handle binding to superior node
  const handleBindToSuperior = async (e) => {
    e.preventDefault();
    
    if (!wallet.connected || !selectedNode) {
      return;
    }
    
    if (!superiorCode.trim()) {
      setError('Please enter a valid superior node reference code');
      return;
    }
    
    try {
      setBindingInProgress(true);
      setError('');
      setSuccess('');
      
      // Convert network name to numeric ID if needed
      let networkId = wallet.networkId || 1;
      if (typeof networkId === 'string') {
        if (networkId === 'ethereum') networkId = 1;
        else if (networkId === 'bsc') networkId = 2;
        else if (networkId === 'solana') networkId = 3;
        else if (!isNaN(parseInt(networkId))) networkId = parseInt(networkId);
      }
      
      // Submit binding request
      const response = await nodeManagementApi.bindToSuperiorNode(
        superiorCode,
        selectedNode.id,
        networkId,
        wallet.type,
        true // Flag to indicate we're using node ID
      );
      
      if (response.success && response.data) {
        setBindingResult(response.data);
        setSuccess(`Successfully bound node ${selectedNode.reference_code} to superior node!`);
        setSuperiorCode('');
        
        // Update the relationship status for this node
        setNodeRelationships(prev => ({
          ...prev,
          [selectedNode.id]: {
            isBound: true,
            relationshipData: response.data
          }
        }));
        
        // Refresh nodes data
        if (refreshNodes) {
          setTimeout(() => {
            refreshNodes();
          }, 1500);
        }
      } else {
        throw new Error(response.message || 'Failed to bind to superior node');
      }
    } catch (err) {
      console.error('Failed to bind to superior node:', err);
      setError(err.message || 'Failed to bind to superior node');
    } finally {
      setBindingInProgress(false);
    }
  };
  
  // Get node tier information
  const getNodeTier = (node) => {
    return node?.tier_name || node?.tier || 'Standard';
  };
  
  // Get relationship status for the selected node
  const getRelationshipStatus = () => {
    if (!selectedNode) return null;
    
    return nodeRelationships[selectedNode.id] || { isBound: false, relationshipData: null };
  };
  
  const relationshipStatus = getRelationshipStatus();
  const isSelectedNodeBound = relationshipStatus?.isBound;
  const selectedNodeRelationship = relationshipStatus?.relationshipData;
  
  if (!wallet.connected) {
    return (
      <div className="section-placeholder">
        <p>Please connect your wallet to manage your nodes.</p>
      </div>
    );
  }
  
  if (isCheckingStatus) {
    return (
      <div className="section-loading">
        <LoadingSpinner />
        <p>Checking node relationship status...</p>
      </div>
    );
  }
  
  return (
    <div className="node-management-section">
      <div className="section-header">
        <h2>Node Relationship Management</h2>
        <p className="section-description">
          Bind your nodes to superior nodes to establish hierarchical relationships that affect network stability maintenance fee distribution.
        </p>
      </div>
      
      {activatedNodes.length > 0 ? (
        <div className="nodes-grid">
          {activatedNodes.map(node => {
            const nodeRelationship = nodeRelationships[node.id] || { isBound: false, relationshipData: null };
            const isBound = nodeRelationship.isBound;
            const relationshipData = nodeRelationship.relationshipData;
            
            return (
              <div 
                key={node.id}
                className={`node-card ${selectedNode?.id === node.id ? 'selected' : ''} ${isBound ? 'bound' : ''}`}
                onClick={() => onNodeChange(node)}
              >
                <div className="node-card-header">
                  <div className="node-code">{node.reference_code}</div>
                  <div className="node-tier">{getNodeTier(node)}</div>
                  {isBound && (
                    <div className="relationship-badge">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17 21V19C17 16.7909 15.2091 15 13 15H5C2.79086 15 1 16.7909 1 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M23 21V19C22.9986 17.1771 21.765 15.5857 20 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M16 3.13C17.7699 3.58317 19.0078 5.17799 19.0078 7.005C19.0078 8.83201 17.7699 10.4268 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <span>Bound</span>
                    </div>
                  )}
                </div>
                
                <div className="node-card-body">
                  <div className="node-status">
                    <span className="status-label">Status:</span>
                    <span className={`status-badge ${node.status}`}>{node.status}</span>
                  </div>
                  
                  <div className="node-relationship-status">
                    <span className="relationship-label">Relationship:</span>
                    {isBound ? (
                      <span className="relationship-bound">Bound to Superior</span>
                    ) : (
                      <span className="relationship-unbound">Not Bound</span>
                    )}
                  </div>
                  
                  {isBound && relationshipData && relationshipData.superior_node && (
                    <div className="superior-info">
                      <div className="superior-code">
                        <span className="superior-label">Superior:</span>
                        <span>{relationshipData.superior_node.reference_code}</span>
                      </div>
                      <div className="relationship-level">
                        <span className="level-label">Level:</span>
                        <span className="level-value">{relationshipData.relationship_level}</span>
                      </div>
                      <div className="relationship-date">
                        <span className="date-label">Since:</span>
                        <span className="date-value">{formatDate(relationshipData.created_at)}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="no-nodes-message">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="info-icon">
            <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <h3>No Activated Nodes</h3>
          <p>You don't have any activated nodes yet. Apply for a node to start managing relationships.</p>
          <a href="/node-application" className="btn btn-primary">Apply for a Node</a>
        </div>
      )}
      
      {selectedNode && activatedNodes.length > 0 && (
        <div className="binding-section">
          <h3>{isSelectedNodeBound ? 'Node Relationship Details' : 'Bind to Superior Node'}</h3>
          
          {/* Display existing relationship if bound */}
          {isSelectedNodeBound && selectedNodeRelationship ? (
            <div className="relationship-details-card">
              <div className="relationship-header">
                <div className="success-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18457 2.99721 7.13633 4.39828 5.49707C5.79935 3.85782 7.69279 2.71538 9.79619 2.24015C11.8996 1.76491 14.1003 1.98234 16.07 2.86" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 4L12 14.01L9 11.01" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4>Node Relationship Established</h4>
              </div>
              
              <div className="relationship-grid">
                <div className="relationship-item">
                  <div className="detail-label">Your Node:</div>
                  <div className="detail-value">{selectedNode.reference_code}</div>
                </div>
                <div className="relationship-item">
                  <div className="detail-label">Superior Node:</div>
                  <div className="detail-value">{selectedNodeRelationship.superior_node?.reference_code}</div>
                </div>
                <div className="relationship-item">
                  <div className="detail-label">Superior Tier:</div>
                  <div className="detail-value">{selectedNodeRelationship.superior_node?.tier}</div>
                </div>
                <div className="relationship-item">
                  <div className="detail-label">Superior Wallet:</div>
                  <div className="detail-value">{formatWalletAddress(selectedNodeRelationship.superior_node?.wallet_address)}</div>
                </div>
                <div className="relationship-item">
                  <div className="detail-label">Relationship Level:</div>
                  <div className="detail-value">{selectedNodeRelationship.relationship_level}</div>
                </div>
                <div className="relationship-item">
                  <div className="detail-label">Created At:</div>
                  <div className="detail-value">{formatDate(selectedNodeRelationship.created_at)}</div>
                </div>
              </div>
              
              <div className="relationship-note">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>This relationship is permanent and cannot be changed.</p>
              </div>
            </div>
          ) : (
            // Display binding form if not bound
            <form onSubmit={handleBindToSuperior} className="binding-form-card">
              <p className="binding-description">
                Enter the reference code of the superior node you want your selected node to join. This will establish a hierarchical relationship that affects network stability maintenance fee distribution.
              </p>
              
              <div className="form-group">
                <label htmlFor="superiorCode">Superior Node Reference Code</label>
                <div className="input-with-button">
                  <input
                    type="text"
                    id="superiorCode"
                    placeholder="ALD-XXXXX"
                    value={superiorCode}
                    onChange={(e) => setSuperiorCode(e.target.value)}
                    disabled={bindingInProgress}
                  />
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={bindingInProgress || !superiorCode.trim()}
                  >
                    {bindingInProgress ? (
                      <>
                        <LoadingSpinner size="small" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      'Bind to Node'
                    )}
                  </button>
                </div>
              </div>
              
              {error && <ErrorMessage message={error} />}
              {success && <SuccessMessage message={success} />}
              
              <div className="binding-note">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 9v2m0 4h.01m8.99-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <p>
                  <strong>Important:</strong> This action cannot be undone. Once bound to a superior node, the relationship is permanent.
                </p>
              </div>
            </form>
          )}
        </div>
      )}
      
      <div className="relationship-advantages">
        <h3>Advantages of Node Relationships</h3>
        <div className="advantages-grid">
          <div className="advantage-card">
            <div className="advantage-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8C10.9 8 10 7.1 10 6C10 4.9 10.9 4 12 4C13.1 4 14 4.9 14 6C14 7.1 13.1 8 12 8ZM12 10C14.2 10 16 8.2 16 6C16 3.8 14.2 2 12 2C9.8 2 8 3.8 8 6C8 8.2 9.8 10 12 10ZM18 14C16.9 14 16 13.1 16 12C16 10.9 16.9 10 18 10C19.1 10 20 10.9 20 12C20 13.1 19.1 14 18 14ZM18 16C20.2 16 22 14.2 22 12C22 9.8 20.2 8 18 8C17.3 8 16.6 8.2 16 8.5C15.7 9.6 15.1 10.6 14.2 11.3C14.7 13 16.2 14.2 18 14.2C18 14.2 18 16 18 16ZM6 14C7.1 14 8 13.1 8 12C8 10.9 7.1 10 6 10C4.9 10 4 10.9 4 12C4 13.1 4.9 14 6 14ZM6 16C3.8 16 2 14.2 2 12C2 9.8 3.8 8 6 8C6.7 8 7.4 8.2 8 8.5C8.4 9.6 9 10.7 9.8 11.4C9.3 13 7.8 14.2 6 14.2C6 14.2 6 16 6 16ZM15.5 22H8.5C7.7 22 7 21.3 7 20.5V19H8V20.5C8 20.8 8.2 21 8.5 21H15.5C15.8 21 16 20.8 16 20.5V19H17V20.5C17 21.3 16.3 22 15.5 22Z" stroke="currentColor" strokeWidth="2"/>
              </svg>
            </div>
            <h4>Network Growth</h4>
            <p>Binding your node to a superior node establishes you in a network hierarchy, enabling faster team growth through coordinated efforts.</p>
          </div>
          
          <div className="advantage-card">
            <div className="advantage-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h4>Network Stability Fees</h4>
            <p>When positioned in the hierarchy with a higher tier superior node, you can benefit from network stability maintenance fee distributions from both direct and indirect subordinates.</p>
          </div>
          
          <div className="advantage-card">
            <div className="advantage-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15 9H9V15H15V9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h4>Stability Maintenance</h4>
            <p>Nodes in established networks participate in stability maintenance fee distributions, providing additional passive income opportunities when RWA points are generated.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NodeManagement;
